import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FileDown, FileX, FileCheck, Printer, Loader2, Send } from 'lucide-react';
import { SendConsentLinkModal } from '../../components/SendConsentLinkModal';
import { UploadSignedConsent } from '../../components/UploadSignedConsent';
import { toast } from 'sonner';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../api';
import { printBlob } from '../../utils/fillable';
import { EXACT_FORMS, fetchExactPdf } from '../../utils/exactForms';

const save = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

// The agency's own Admission Consent / Admission Packet, filled from the chart and uploads; wording and layout untouched
export default function ExactFormPage() {
  const { formId } = useParams();
  const [params, setParams] = useSearchParams();
  const patientId = params.get('patientId') || '';
  const form = EXACT_FORMS[formId];
  const [patients, setPatients] = useState([]);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState('');
  const [sending, setSending] = useState(false);
  const [signReq, setSignReq] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const canSign = formId === 'admission_consent_exact' && Boolean(patientId);
  useEffect(() => {
    if (!canSign || sending) return;
    api.get(`/consent-sign/requests?patientId=${patientId}`).then(r => setSignReq(r.data.requests[0] || null)).catch(() => setSignReq(null));
  }, [canSign, patientId, sending, refresh]);

  useEffect(() => { api.get('/patients').then(r => setPatients(r.data.patients || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!form || !patientId) { setPreview(''); return undefined; }
    let url = '';
    fetchExactPdf(formId, patientId, null, 'prefill').then(b => { url = URL.createObjectURL(b); setPreview(url); }).catch(() => toast.error('Could not load the form'));
    return () => url && URL.revokeObjectURL(url);
  }, [formId, patientId, form]);

  if (!form) return <DashboardLayout><p className="p-8">Unknown form.</p></DashboardLayout>;
  const patient = patients.find(p => p.id === patientId);
  const last = patient?.lastName || 'patient';
  const run = async (mode) => {
    setBusy(mode);
    try {
      const blob = await fetchExactPdf(formId, mode === 'blank' ? null : patientId, null, mode);
      if (mode === 'print') printBlob(blob);
      else save(blob, form.file.replace('.pdf', mode === 'blank' ? '_blank.pdf' : mode === 'flat' ? `_${last}_filled.pdf` : `_${last}.pdf`));
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };
  const btn = 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-white/10 hover:bg-white/20 disabled:opacity-50 transition-colors';
  const icon = (m, I) => (busy === m ? <Loader2 className="w-4 h-4 animate-spin" /> : <I className="w-4 h-4" />);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4" data-testid={`exact-form-${formId}`}>
        <div className="bg-[#1e3a8a] text-white rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">{form.name}</h1>
            <p className="text-xs text-white/80">Your agency's form exactly as uploaded. Only the boxes are filled in.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => run('blank')} disabled={!!busy} className={btn} data-testid="exact-blank-pdf">{icon('blank', FileX)} Blank PDF</button>
            <button onClick={() => run('prefill')} disabled={!!busy || !patientId} className={btn} data-testid="exact-editable-pdf">{icon('prefill', FileDown)} Editable PDF</button>
            <button onClick={() => run('flat')} disabled={!!busy || !patientId} className={btn} data-testid="exact-filled-pdf">{icon('flat', FileCheck)} Filled PDF</button>
            <button onClick={() => run('print')} disabled={!!busy || !patientId} className={btn} data-testid="exact-print">{icon('print', Printer)} Print</button>
            {canSign && <button onClick={() => setSending(true)} disabled={!patient} className={`${btn} !bg-[#d4af37] text-gray-900`} data-testid="exact-send-sign-link"><Send className="w-4 h-4" /> Send for Patient Signature</button>}
            {canSign && <UploadSignedConsent patientId={patientId} btnClass={btn} onSaved={() => setRefresh(n => n + 1)} />}
          </div>
        </div>
        <label className="block text-sm text-gray-700">Patient
          <select value={patientId} onChange={e => setParams(e.target.value ? { patientId: e.target.value } : {})} className="mt-1 block w-full md:w-96 border rounded-lg px-3 py-2" data-testid="exact-patient-select">
            <option value="">Select a patient…</option>
            {patients.map(p => <option key={p.id} value={p.id}>{p.lastName}, {p.firstName} · MR# {p.medicalRecordNumber || ''}</option>)}
          </select>
        </label>
        {canSign && signReq && (
          <p className="text-sm text-gray-700" data-testid="consent-sign-status">
            {signReq.channel === 'upload' ? `Signed consent uploaded ${new Date(signReq.createdAt).toLocaleString()}: ` : `Signing link (${signReq.channel}) sent ${new Date(signReq.createdAt).toLocaleString()}: `}
            <strong className="capitalize">{signReq.status}</strong>
            {signReq.status === 'signed' && ` by ${signReq.signedName}. The signed copy is in Application Packages.`}
            {signReq.channel === 'upload' && signReq.signatureVerified === false && ' (signature not verified automatically)'}</p>
        )}
        {sending && patient && <SendConsentLinkModal patient={patient} onClose={() => setSending(false)} />}
        {preview ? <iframe title={form.name} src={preview} className="w-full h-[75vh] border rounded-lg bg-white" data-testid="exact-preview" />
          : <p className="text-sm text-gray-500" data-testid="exact-no-patient">{patientId ? 'Loading the form…' : 'Select a patient to see the filled form, or download the blank form.'}</p>}
      </div>
    </DashboardLayout>
  );
}
